/**
 * Single source of truth for the Shoorah super-admin dashboard.
 *
 * The sibling of utils/dashboardFeatures.js, and deliberately the same shape —
 * the plane components (PlaneHeader, StatCard, SelectMoreCard,
 * FeaturePickerDialog) read these fields and do not care which panel they are
 * rendering for.
 *
 * WHAT A CARD IS HERE
 * -------------------
 * On the B2B dashboard a card is a *feature*, because a company admin's
 * question is "is my workforce using what we pay for". A super admin's
 * question is different — "how is the platform doing" — so a card here is a
 * *metric about the business*, grouped by the population it describes.
 *
 * The Shoorah feature hubs (Sleep, Breathwork, Mood, …) are therefore NOT
 * cards on this dashboard. They are their own sidebar sections with their own
 * combined / B2B / B2C pages, exactly as Sleep and Breathwork already are. Put
 * differently: the B2B dashboard is a feature index; this one is a business
 * overview, and the features are one level down.
 *
 * FIELDS
 * `key`      matches the field the aggregate endpoint returns, and the
 *            :planeKey route param.
 * `plane`    which band the card renders in.
 * `unit`     the noun that follows the headline metric.
 * `format`   how to render the raw number — 'count' (default) or 'money'.
 *            Revenue cards are pounds, and a pound sign belongs to the data,
 *            not to a one-off in the dashboard component.
 * `manage`   the existing super-admin page this card opens. No :slug
 *            substitution — super-admin routes are absolute. null means the
 *            figure has no page of its own yet.
 * `featured` a card shown on the band itself; everything else is reachable
 *            through "Select more". Four featured cards plus "Select more"
 *            fills a five-column row, which is the most a band should carry.
 * `group`    heading inside the "Select more" dialog.
 *
 * A plane may also carry `viewAll: { label, to }`, which puts a link on the
 * right of its heading — the band is a summary, and this is the way out to the
 * section behind it. A plane that also sets `viewAllOwnsExtras` drops its
 * "Select more" card: its non-featured figures live on that section's own
 * dashboard instead, which is a page rather than a dialog.
 *
 * `overviewCharts` puts a row of four donuts under the band's cards — a glance
 * at who its population is, next to how many of them there are.
 */
import { STAT_ICONS, FEATURE_ICONS } from '../component/common/designSystem';

export const ADMIN_PLANE = {
  B2C: 'b2c',
  COMPANIES: 'companies',
  PEAP: 'peap',
  PARTNERS: 'partners',
  REVENUE: 'revenue'
};

/**
 * Plane accents.
 *
 * Five hues that must stay apart from each other, taken from CHART_PALETTE in
 * designSystem/theme.js — which keeps a band's accent and the colours inside
 * its charts from drifting apart.
 *
 * Green and red are deliberately not here: those two mean status everywhere
 * else in the panel, and a band that is permanently "green" would read as a
 * band that is permanently fine.
 */
export const ADMIN_PLANE_META = {
  [ADMIN_PLANE.B2C]: {
    label: 'Shoorah Users',
    note: 'Everyone on Shoorah, however they got here',
    accent: '#7B72E9', // CHART_COLORS.indigo
    // The dashboard's own band is a summary; "View all" leads to the fuller
    // set of figures, not to the list. The list is reached from People
    // Management > Users, by an admin who came looking for a person.
    viewAll: { label: 'View all', to: '/users/dashboard' },
    viewAllOwnsExtras: true,
    overviewCharts: true
  },
  [ADMIN_PLANE.COMPANIES]: {
    label: 'B2B companies',
    note: 'Organisations on a plan, and the seats they bought',
    accent: '#3B9EF5', // CHART_COLORS.blue
    viewAll: { label: 'View all', to: '/B2B-company-list' }
  },
  [ADMIN_PLANE.PEAP]: {
    label: 'PEAP',
    note: 'Specialists, sessions and the wellpoints that pay for them',
    accent: '#2FC9CD', // CHART_COLORS.brand
    viewAll: { label: 'View all', to: '/peap-management' }
  },
  [ADMIN_PLANE.PARTNERS]: {
    label: 'Partners',
    note: 'Introducers, what they brought in, and what they earned',
    accent: '#F0913C', // CHART_COLORS.orange
    viewAll: { label: 'View all', to: '/partners' }
  },
  [ADMIN_PLANE.REVENUE]: {
    label: 'Revenue',
    note: 'What the platform earns, and where it comes from',
    accent: '#E8467C' // CHART_COLORS.pink
  }
};

export const ADMIN_PLANE_ORDER = [
  ADMIN_PLANE.B2C,
  ADMIN_PLANE.COMPANIES,
  ADMIN_PLANE.PEAP,
  ADMIN_PLANE.PARTNERS,
  ADMIN_PLANE.REVENUE
];

/** Headings inside the "Select more" dialog, in the order they render. */
export const ADMIN_GROUP = {
  POPULATION: 'Population',
  ENGAGEMENT: 'Engagement',
  CAPACITY: 'Seats and plans',
  DELIVERY: 'Service delivery',
  GROWTH: 'Growth',
  COMMERCIAL: 'Commercial'
};

export const ADMIN_GROUP_ORDER = [
  ADMIN_GROUP.POPULATION,
  ADMIN_GROUP.ENGAGEMENT,
  ADMIN_GROUP.CAPACITY,
  ADMIN_GROUP.DELIVERY,
  ADMIN_GROUP.GROWTH,
  ADMIN_GROUP.COMMERCIAL
];

export const ADMIN_DASHBOARD_CARDS = [
  // ---------------- Shoorah users ----------------
  //
  // The four featured cards are the whole consumer base split by where each
  // person came from, plus how many of all of them showed up this month.
  // "Shoorah Users" therefore means people who came to Shoorah directly —
  // employees and club members are the other two cards, not part of this one.
  {
    key: 'b2c_total',
    name: 'Shoorah Users',
    plane: ADMIN_PLANE.B2C,
    unit: 'users',
    icon: STAT_ICONS.users,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: '/users'
  },
  {
    key: 'users_b2b',
    name: 'B2B Users',
    plane: ADMIN_PLANE.B2C,
    unit: 'users',
    icon: STAT_ICONS.company,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: '/users'
  },
  {
    // People who reach Shoorah through an organisation they belong to — a
    // club, a union — rather than through an employer who bought seats.
    key: 'users_members',
    name: 'Members',
    plane: ADMIN_PLANE.B2C,
    unit: 'members',
    icon: STAT_ICONS.seats,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: '/users'
  },
  {
    // All three populations together, which is why it is not `b2c_active_month`
    // — that key still exists below and still counts direct users only.
    key: 'users_active_month',
    name: 'Active this month',
    plane: ADMIN_PLANE.B2C,
    unit: 'users',
    icon: STAT_ICONS.activeUsers,
    featured: true,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: '/users'
  },
  {
    key: 'b2c_new_month',
    name: 'New this month',
    plane: ADMIN_PLANE.B2C,
    unit: 'joined',
    icon: STAT_ICONS.newUsers,
    group: ADMIN_GROUP.GROWTH,
    manage: '/users'
  },
  {
    key: 'b2c_active_month',
    name: 'Shoorah users active',
    plane: ADMIN_PLANE.B2C,
    unit: 'users',
    icon: STAT_ICONS.activeUsers,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: '/users'
  },
  {
    key: 'b2c_active_today',
    name: 'Active today',
    plane: ADMIN_PLANE.B2C,
    unit: 'users',
    icon: STAT_ICONS.onlineNow,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: null
  },
  {
    key: 'b2c_subscribed',
    name: 'Paid subscriptions',
    plane: ADMIN_PLANE.B2C,
    unit: 'plans',
    icon: STAT_ICONS.subscription,
    group: ADMIN_GROUP.CAPACITY,
    manage: null
  },
  {
    // Kids profiles sit under a parent's B2C account rather than being
    // accounts of their own, so they are counted here and never added to the
    // user totals above — doing so would double-count one household.
    key: 'b2c_kids',
    name: 'Kids profiles',
    plane: ADMIN_PLANE.B2C,
    unit: 'profiles',
    icon: FEATURE_ICONS.guided_path,
    group: ADMIN_GROUP.POPULATION,
    manage: '/kids-list'
  },

  // ---------------- B2B companies ----------------
  {
    key: 'companies_total',
    name: 'Companies',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'companies',
    icon: STAT_ICONS.company,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: '/B2B-company-list'
  },
  {
    key: 'seats_bought',
    name: 'Seats bought',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'seats',
    icon: STAT_ICONS.seats,
    featured: true,
    group: ADMIN_GROUP.CAPACITY,
    manage: '/B2B-company-list'
  },
  {
    key: 'b2b_users',
    name: 'B2B users',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'users',
    icon: STAT_ICONS.users,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: null
  },
  {
    // Bought minus filled. Kept as its own card rather than a percentage on
    // the seats card, because it is the number a renewal conversation opens
    // with.
    key: 'empty_seats',
    name: 'Empty seats',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'seats',
    icon: STAT_ICONS.seats,
    group: ADMIN_GROUP.CAPACITY,
    manage: null
  },
  {
    key: 'companies_active',
    name: 'Active companies',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'companies',
    icon: STAT_ICONS.onlineNow,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: '/B2B-company-list'
  },
  {
    key: 'child_companies',
    name: 'Child companies',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'companies',
    icon: STAT_ICONS.company,
    group: ADMIN_GROUP.POPULATION,
    manage: '/B2B-company-list'
  },
  {
    // Seats bought times seat price, summed. A fact about what is contracted,
    // which is why it can be shown while the cash figures on the Revenue plane
    // still cannot — those need the Stripe reconciliation.
    key: 'companies_contract_value',
    name: 'Contracted value',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'across all plans',
    format: 'money',
    icon: STAT_ICONS.revenue,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/B2B-earning'
  },
  {
    key: 'companies_new_month',
    name: 'New this month',
    plane: ADMIN_PLANE.COMPANIES,
    unit: 'onboarded',
    icon: STAT_ICONS.newUsers,
    group: ADMIN_GROUP.GROWTH,
    manage: '/B2B-company-list'
  },

  // ---------------- PEAP ----------------
  {
    key: 'peap_specialists',
    name: 'Active specialists',
    plane: ADMIN_PLANE.PEAP,
    unit: 'specialists',
    icon: STAT_ICONS.specialist,
    featured: true,
    group: ADMIN_GROUP.DELIVERY,
    manage: '/peap-management'
  },
  {
    key: 'peap_sessions',
    name: 'Sessions',
    plane: ADMIN_PLANE.PEAP,
    unit: 'sessions',
    icon: STAT_ICONS.session,
    featured: true,
    group: ADMIN_GROUP.DELIVERY,
    manage: '/peap-dashboard'
  },
  {
    key: 'peap_wellpoints_issued',
    name: 'Wellpoints issued',
    plane: ADMIN_PLANE.PEAP,
    unit: 'wellpoints',
    icon: FEATURE_ICONS.peap,
    featured: true,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/peap-B2B-wellPoints'
  },
  {
    key: 'peap_pending_specialists',
    name: 'Awaiting approval',
    plane: ADMIN_PLANE.PEAP,
    unit: 'specialists',
    icon: STAT_ICONS.goals,
    group: ADMIN_GROUP.DELIVERY,
    manage: '/peap-management'
  },
  {
    key: 'peap_clients',
    name: 'Clients seen',
    plane: ADMIN_PLANE.PEAP,
    unit: 'clients',
    icon: STAT_ICONS.users,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: '/peap-dashboard'
  },
  {
    key: 'peap_wellpoints_spent',
    name: 'Wellpoints redeemed',
    plane: ADMIN_PLANE.PEAP,
    unit: 'wellpoints',
    icon: FEATURE_ICONS.peap,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/peap-B2B-wellPoints'
  },

  // ---------------- Partners ----------------
  {
    key: 'partners_total',
    name: 'Partners',
    plane: ADMIN_PLANE.PARTNERS,
    unit: 'partners',
    icon: STAT_ICONS.partner,
    featured: true,
    group: ADMIN_GROUP.POPULATION,
    manage: '/partners'
  },
  {
    key: 'partner_introduced',
    name: 'Companies introduced',
    plane: ADMIN_PLANE.PARTNERS,
    unit: 'introductions',
    icon: STAT_ICONS.company,
    featured: true,
    group: ADMIN_GROUP.GROWTH,
    manage: '/partner-introduced'
  },
  {
    key: 'partner_earnings',
    name: 'Partner earnings',
    plane: ADMIN_PLANE.PARTNERS,
    unit: 'paid out',
    format: 'money',
    icon: STAT_ICONS.revenue,
    featured: true,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/partner-earning'
  },
  {
    key: 'partners_active',
    name: 'Active partners',
    plane: ADMIN_PLANE.PARTNERS,
    unit: 'partners',
    icon: STAT_ICONS.onlineNow,
    group: ADMIN_GROUP.ENGAGEMENT,
    manage: '/partners'
  },
  {
    // An introduction that became a paying company. The ratio of this to
    // partner_introduced is how a partner is actually judged.
    key: 'partner_converted',
    name: 'Introductions converted',
    plane: ADMIN_PLANE.PARTNERS,
    unit: 'companies',
    icon: STAT_ICONS.completions,
    group: ADMIN_GROUP.GROWTH,
    manage: '/partner-introduced'
  },

  // ---------------- Revenue ----------------
  {
    key: 'revenue_total',
    name: 'Total revenue',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'this period',
    format: 'money',
    icon: STAT_ICONS.revenue,
    featured: true,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/earning'
  },
  {
    key: 'revenue_b2b',
    name: 'B2B revenue',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'this period',
    format: 'money',
    icon: STAT_ICONS.company,
    featured: true,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/B2B-earning'
  },
  {
    key: 'revenue_b2c',
    name: 'B2C revenue',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'this period',
    format: 'money',
    icon: STAT_ICONS.users,
    featured: true,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/earning'
  },
  {
    key: 'revenue_subscriptions',
    name: 'Active subscriptions',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'plans',
    icon: STAT_ICONS.subscription,
    group: ADMIN_GROUP.CAPACITY,
    manage: '/earning'
  },
  {
    key: 'revenue_marketplace',
    name: 'Marketplace orders',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'orders',
    icon: FEATURE_ICONS.marketplace,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/stats/marketplace/egifts'
  },
  {
    key: 'revenue_vouchers',
    name: 'Voucher orders',
    plane: ADMIN_PLANE.REVENUE,
    unit: 'orders',
    icon: FEATURE_ICONS.perks,
    group: ADMIN_GROUP.COMMERCIAL,
    manage: '/vouchers'
  }
];

/* ------------------------------------------------------------------ *
 * Selectors — same names and semantics as dashboardFeatures.js, so the
 * plane components can be pointed at either registry.
 * ------------------------------------------------------------------ */

export const getAdminCardsByPlane = (plane) =>
  ADMIN_DASHBOARD_CARDS.filter((card) => card.plane === plane);

export const getAdminCardByKey = (key) =>
  ADMIN_DASHBOARD_CARDS.find((card) => card.key === key) || null;

/** The three cards shown on a plane. */
export const getAdminFeaturedByPlane = (plane) =>
  getAdminCardsByPlane(plane).filter((card) => card.featured);

/** Everything else on that plane, reached via "Select more". */
export const getAdminMoreByPlane = (plane) =>
  getAdminCardsByPlane(plane).filter((card) => !card.featured);

/**
 * Super-admin routes carry no :slug, so this is just the route — but it stays
 * a function so the dashboard component can call the same helper whichever
 * registry it was handed.
 */
export const resolveAdminRoute = (card) => card?.manage || null;
